import { useEffect, useState } from "react";
import PageHeading from "../../components/PageElements/PageHeading";
import Table from "../../components/Tables/Table";
import { Form, Modal, Select, Space, Tag, Tooltip } from "antd";
import { FiEdit, FiTrash2 } from "react-icons/fi";
import toast from "react-hot-toast";
import TableHeader from "../../components/Tables/TableHeader";
import TextInput from "../../components/Inputs/TextInput";
import FormButton from "../../components/Buttons/FormButton";
import { Formik } from "formik";
import FormCancelButton from "../../components/Buttons/FormCancelButton";
import FormErrors from "../../components/Errors/FormErrors";
import DeleteModal from "../../components/Modals/DeleteModal";
import { IoIosWarning } from "react-icons/io";
import CustomDropDownInput from "../../components/Inputs/CustomDropDownInput";
import { getInstitutions } from "../../services/institutionService";
import {
  createAffiliation,
  deleteAffiliation,
  getAffiliations,
  updateAffiliation,
} from "../../services/affiliationService";
import { affiliationSchema } from "../../validators/affiliationValidators";
import { useStateContext } from "../../context/ContextProvider";
import { isInstitutionAdmin } from "../../utils/authHelpers";

const Affiliation = () => {
  const [affiliations, setAffiliations] = useState([]);
  const [filteredAffiliations, setFilteredAffiliations] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [affiliationToEdit, setAffiliationToEdit] = useState(null);
  const [modalText, setModalText] = useState("");
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [affiliationToDelete, setAffiliationToDelete] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [errs, setErrs] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [selectedInstitution, setSelectedInstitution] =
    useState("Not Selected");

  const { user } = useStateContext();
  const isInstAdmin = isInstitutionAdmin(user);

  useEffect(() => {
    loadAffiliations();
    loadInstitutions();
  }, []);

  const statusOptions = [
    { id: "active", name: "Active" },
    { id: "inactive", name: "Inactive" },
  ];

  useEffect(() => {
    let filtered = affiliations;
    if (searchTerm.trim() !== "") {
      filtered = affiliations.filter((item) =>
        `${item?.organization_name} ${item?.institution?.name}`
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );
    }

    if (selectedInstitution !== "Not Selected") {
      filtered = filtered.filter(
        (item) =>
          item.institution?.id?.toString() === selectedInstitution?.toString()
      );
    }

    setFilteredAffiliations(filtered);
  }, [searchTerm, affiliations, selectedInstitution]);

  const loadInstitutions = async () => {
    try {
      const { data } = await getInstitutions();
      setInstitutions(data.data);
    } catch (error) {
      toast.error("Failed to load institutions");
    }
  };

  const loadAffiliations = async () => {
    setLoading(true);
    try {
      const { data } = await getAffiliations();

      setAffiliations(data.data);
      setFilteredAffiliations(data.data);
    } catch (error) {
      toast.error(
        error?.response?.data?.error || "Failed to load affiliations"
      );
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = (payload, resetForm) => {
    if (affiliationToEdit) {
      onUpdateAffiliation(payload, resetForm);
    } else {
      onCreateAffiliation(payload, resetForm);
    }
  };

  const onCreateAffiliation = async (payload, resetForm) => {
    setErrs(null);

    try {
      const { data } = await createAffiliation(payload);

      setAffiliations((prev) => [...prev, data.data]);

      toast.success("Affiliation added successfully");

      setIsModalOpen(false);

      resetForm();
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response.data.error || "Affiliation was not added, try again!"
        );
      }
    }
  };

  const handleEdit = (category) => {
    setAffiliationToEdit(category);
    setIsModalOpen(true);
  };

  const onUpdateAffiliation = async (payload, resetForm) => {
    setErrs(null);

    try {
      const originalAffiliations = [...affiliations];

      const affiliationIndex = originalAffiliations.findIndex(
        (affiliation) => affiliation.id === affiliationToEdit.id
      );

      if (affiliationIndex === -1) throw new Error("Affiliation not found.");

      const { data } = await updateAffiliation(affiliationToEdit.id, payload);

      originalAffiliations[affiliationIndex] = data.data;

      setAffiliations(originalAffiliations);

      setAffiliationToEdit(null);

      resetForm();

      setIsModalOpen(false);

      toast.success("Affiliation updated successfully!");
    } catch (error) {
      const { response } = error;
      if (response && response.status === 422) {
        setErrs(response.data.errors);
      } else {
        toast.error(
          response?.data?.error || "Affiliation not updated, try again!"
        );
      }
    }
  };

  const handleDelete = (payload) => {
    setModalText(
      `Please confirm that you want to delete %${payload?.name?.toUpperCase()}`
    );
    setAffiliationToDelete(payload);
    setIsDeleteModalOpen(true);
  };

  const onDeleteAffiliation = async () => {
    const originalAffiliations = [...affiliations];

    const newAffiliations = originalAffiliations.filter(
      (affiliation) => affiliation.id !== affiliationToDelete.id
    );

    setAffiliations(newAffiliations);

    try {
      await deleteAffiliation(affiliationToDelete?.id);

      toast.success("Affiliation deleted successfully!");
    } catch (error) {
      const { response } = error;
      toast.error(
        ` ${
          response?.data?.message ||
          error?.response?.data?.error ||
          "Affiliation not deleted, try again later"
        }`
      );

      setAffiliations(originalAffiliations);
    } finally {
      setIsDeleteModalOpen(false);

      setAffiliationToDelete(null);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setIsDeleteModalOpen(false);
    setAffiliationToEdit(null);
    setAffiliationToDelete(null);
    setErrs(null);
  };

  const columns = [
    {
      title: "#",
      render: (text, record, index) => index + 1,
      width: 70,
    },
    ...(!isInstAdmin
      ? [
          {
            title: "Institution",
            dataIndex: ["institution", "name"],
            key: "institution",
            render: (text) => (
              <span className="text-gray-700 text-sm">{text || "N/A"}</span>
            ),
          },
        ]
      : []),
    {
      title: "Organization name",
      dataIndex: "organization_name",
      key: "organization_name",
    },
    {
      title: "Membership Status",
      render: (_, record) => (
        <Tag
          color={
            record?.membership_status.toLowerCase() === "active"
              ? "green"
              : "red"
          }
        >
          {record?.membership_status ? "Active" : "Inactive"}
        </Tag>
      ),
    },

    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Edit Affiliation">
            <button
              className="p-2 border border-green-200 rounded-md text-green-600 hover:bg-green-50 transition cursor-pointer"
              onClick={() => handleEdit(record)}
            >
              <FiEdit size={16} />
            </button>
          </Tooltip>
          <Tooltip title="Delete Affiliation">
            <button
              className="p-2 border border-red-200 rounded-md text-red-500 hover:bg-red-50 transition cursor-pointer"
              onClick={() => handleDelete(record)}
            >
              <FiTrash2 size={16} />
            </button>
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <PageHeading title="Affiliations" subtitle1="Affiliations" />

      <div className="px-8 py-6">
        <div className="bg-white shadow-sm rounded-lg p-6">
          <TableHeader
            title="Affiliations"
            count={filteredAffiliations.length}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            buttonLabel="+ Add Affiliation"
            onButtonClick={() => setIsModalOpen(true)}
          />

          <Table
            isPaginable
            pageSize={10}
            isSearchable={false}
            loading={loading}
            columns={columns}
            rows={filteredAffiliations.map((item) => ({
              ...item,
              key: item.id,
            }))}
            extra={
              !isInstAdmin && (
                <div className="mb-2 py-2 flex gap-2">
                  <Select
                    multiple={false}
                    id="institutions"
                    defaultValue={selectedInstitution}
                    className="w-full h-[36px] min-w-[250px]"
                    onChange={(value) => {
                      setSelectedInstitution(value);
                    }}
                    showSearch={true}
                    filterOption={(input, option) =>
                      option?.children
                        ?.toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                  >
                    <Select.Option value={"Not Selected"}>
                      Filter by Institution
                    </Select.Option>
                    {institutions &&
                      institutions.length > 0 &&
                      institutions.map((option) => (
                        <Select.Option key={option.id} value={option.id}>
                          {option.name}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
              )
            }
          />

          {/* Modal */}

          <Modal
            title={
              <div className="px-4 py-3">
                <h2 className="text-lg font-semibold text-gray-700">
                  {affiliationToEdit ? "Edit Affiliation" : "Add Affiliation"}
                </h2>
              </div>
            }
            open={isModalOpen}
            onCancel={handleCancel}
            footer={false}
          >
            <Formik
              initialValues={{
                organization_name: affiliationToEdit
                  ? affiliationToEdit?.organization_name
                  : "",
                membership_status: affiliationToEdit
                  ? affiliationToEdit?.membership_status
                  : "",

                institution_id: affiliationToEdit
                  ? affiliationToEdit?.institution?.id
                  : isInstAdmin
                  ? user?.institution?.id
                  : "",
              }}
              enableReinitialize
              validationSchema={affiliationSchema}
              onSubmit={(values, { resetForm }) => {
                onSubmit(values, resetForm);
              }}
            >
              {({
                values,
                handleChange,
                handleBlur,
                errors,
                touched,
                setFieldValue,
                setFieldTouched,
                submitForm,
              }) => {
                return (
                  <Form className="space-y-5 px-4! py-4!">
                    {errs && <FormErrors errors={errs} />}

                    {isInstAdmin ? (
                      <input
                        type="hidden"
                        name="institution_id"
                        value={
                          affiliationToEdit
                            ? affiliationToEdit?.institution?.id
                            : user?.institution?.id
                        }
                      />
                    ) : (
                      <CustomDropDownInput
                        name="institution_id"
                        title="Institution"
                        placeholder="Select an institution"
                        requiredField
                        errors={errors}
                        values={values}
                        touched={touched}
                        displayLabel="name"
                        idLabel="id"
                        options={institutions}
                        setFieldValue={setFieldValue}
                      />
                    )}

                    <TextInput
                      name="organization_name"
                      title="Organization Name"
                      placeholder="e.g. MTN Uganda"
                      requiredField
                      errors={errors}
                      values={values}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />

                    <CustomDropDownInput
                      name="membership_status"
                      title="Membership Status"
                      placeholder="Select a membership status"
                      requiredField
                      errors={errors}
                      values={values}
                      touched={touched}
                      displayLabel="name"
                      idLabel="id"
                      options={statusOptions}
                      setFieldValue={setFieldValue}
                    />

                    <div className="flex justify-end gap-2">
                      <FormCancelButton text="Cancel" onClick={handleCancel} />
                      <FormButton
                        text="Save"
                        type="primary"
                        onClick={submitForm}
                      />
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </Modal>

          <DeleteModal
            open={isDeleteModalOpen}
            handleOk={onDeleteAffiliation}
            handleCancel={handleCancel}
            confirmLoading={confirmLoading}
            modalText={modalText}
            extra={
              <div className="mt-3 bg-red-100 text-red-500 border-[1px] border-red-300 mx-5 px-3 py-2 rounded-md">
                <h3 className="text-red-700 text-md flex gap-1 items-center">
                  <IoIosWarning /> Warning
                </h3>
                Please note that this action cannot be reversed!
              </div>
            }
          />
        </div>
      </div>
    </>
  );
};

export default Affiliation;
